import React from "react";
import styles from "./style.module.scss";

export type RaceBuilderFormState = {
  dataFile: string;
  totalRaceEnergykWh: number | "";
  numberOfLaps: number | "";
  initialSocPct: number | "";
  initialTCellC: number | "";
  ambientC: number | "";
  vCarScaler: number | "";
  lastCornerBufferkWh: number | "";
  pitBoostRace: boolean;
  targetPitSoc: number | "";
  pitScenario: number;
};

export type PitBoostScenario = {
  scenario: number;
  stopTimeS: number | null;
  energyKwh: number | null;
};

type RaceBuilderInputsProps = {
  inputs: RaceBuilderFormState;
  pitBoostScenarios: PitBoostScenario[];
  energyError: string | null;
  onChange: <Key extends keyof RaceBuilderFormState>(
    key: Key,
    value: RaceBuilderFormState[Key],
  ) => void;
};

export default function RaceBuilderInputs({
  inputs,
  pitBoostScenarios,
  energyError,
  onChange,
}: RaceBuilderInputsProps) {
  function updateNumberInput(
    key: Exclude<
      keyof RaceBuilderFormState,
      "dataFile" | "pitBoostRace" | "pitScenario"
    >,
    value: string,
  ) {
    onChange(key, value === "" ? "" : Number(value));
  }

  return (
    <section
      className={`${styles.panel} ${styles.raceBuilderInputs}`}
      aria-labelledby="race-builder-inputs-title"
    >
      <div className={styles.panelHeader}>
        <h2 id="race-builder-inputs-title">Inputs</h2>
      </div>
      <div className={styles.inputGrid}>
        <label className={styles.field}>
          <span>Data file</span>
          <input
            type="text"
            value={inputs.dataFile}
            onChange={(event) => onChange("dataFile", event.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span>Total race energy (kWh)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={inputs.totalRaceEnergykWh}
            disabled={energyError !== null && inputs.totalRaceEnergykWh === ""}
            aria-invalid={energyError !== null}
            aria-describedby={
              energyError ? "race-builder-energy-error" : undefined
            }
            onChange={(event) =>
              updateNumberInput("totalRaceEnergykWh", event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>Number of laps</span>
          <input
            type="number"
            min="1"
            max="1000"
            step="1"
            value={inputs.numberOfLaps}
            onChange={(event) =>
              updateNumberInput("numberOfLaps", event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>Initial SOC (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            step="any"
            value={inputs.initialSocPct}
            onChange={(event) =>
              updateNumberInput("initialSocPct", event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>Initial TCell (°C)</span>
          <input
            type="number"
            step="any"
            value={inputs.initialTCellC}
            onChange={(event) =>
              updateNumberInput("initialTCellC", event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>Ambient temperature (°C)</span>
          <input
            type="number"
            step="any"
            value={inputs.ambientC}
            onChange={(event) =>
              updateNumberInput("ambientC", event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>vCar scaler</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={inputs.vCarScaler}
            onChange={(event) =>
              updateNumberInput("vCarScaler", event.target.value)
            }
          />
        </label>

        <label className={styles.field}>
          <span>Last corner buffer (kWh)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={inputs.lastCornerBufferkWh}
            onChange={(event) =>
              updateNumberInput("lastCornerBufferkWh", event.target.value)
            }
          />
        </label>
      </div>

      {energyError && (
        <p
          id="race-builder-energy-error"
          className={styles.errorMessage}
          role="alert"
        >
          {energyError}
        </p>
      )}

      <div className={styles.pitBoostControls}>
        <label className={styles.pitBoostToggle}>
          <input
            type="checkbox"
            checked={inputs.pitBoostRace}
            onChange={(event) => onChange("pitBoostRace", event.target.checked)}
          />
          <span>Pit boost race</span>
        </label>

        {inputs.pitBoostRace && (
          <div className={styles.pitBoostInputs}>
            <label className={styles.field}>
              <span>Target boost SOC (%)</span>
              <input
                type="number"
                min="0"
                max="100"
                step="any"
                value={inputs.targetPitSoc}
                onChange={(event) =>
                  updateNumberInput("targetPitSoc", event.target.value)
                }
              />
            </label>

            <fieldset className={styles.pitBoostScenarios}>
              <legend>Pit boost scenario</legend>
              {pitBoostScenarios.map(({ scenario, stopTimeS, energyKwh }) => {
                const hasStopTime =
                  stopTimeS !== null &&
                  Number.isFinite(stopTimeS) &&
                  stopTimeS >= 0;
                const hasEnergy =
                  energyKwh !== null && Number.isFinite(energyKwh);
                return (
                  <label
                    key={scenario}
                    className={`${styles.pitBoostScenario} ${
                      !hasStopTime ? styles.unavailableScenario : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="race-builder-pit-scenario"
                      value={scenario}
                      checked={inputs.pitScenario === scenario}
                      disabled={!hasStopTime}
                      onChange={() => onChange("pitScenario", scenario)}
                    />
                    <span>
                      <strong>Scenario {scenario}</strong>
                      <span className={styles.pitBoostScenarioDetails}>
                        {hasStopTime
                          ? `${stopTimeS} s stop`
                          : "Stop time unavailable"}
                        {hasEnergy ? ` · ${energyKwh} kWh` : ""}
                      </span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
          </div>
        )}
      </div>
    </section>
  );
}
